import { Icon } from '@vrplatform/icons';
import {
  Box,
  Collapse,
  Group,
  Stack,
  Text,
  Tooltip,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { ReactNode } from 'react';
import { FeeAccountEditor } from './FeeAccountsEditor';
import { FeeAccountPartyEditor } from './FeeAccountsPartyEditor';
import { FeeTaxRateEditor } from './FeeTaxRateEditor';
import { FeeTriggerEditor } from './FeeTriggerEditor';

export const FeeAdvancedSettings = () => {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const [opened, handlers] = useDisclosure(false);

  const backgroundColor =
    colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.neutral[1];

  const borderRadius = theme.radius.md;

  return (
    <>
      <Group
        justify="space-between"
        component={'button'}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handlers.toggle();
        }}
        sx={() =>
          Object.assign({
            backgroundColor,
            padding: '0.1rem 0.6rem',
            minHeight: 35,
            borderRadius,
            border: 'none',
          })
        }
      >
        <Group wrap="nowrap" gap={rem(8)}>
          <Icon
            color={(theme) => theme.colors.neutral[5]}
            icon="ChevronIcon"
            style={{
              transform: `rotate(${opened ? 0 : -90}deg)`,
              transition: 'transform 100ms ease-in-out',
            }}
            size={16}
          />
          <Text
            size="sm"
            fw={500}
            sx={{ lineHeight: '1.4rem', letterSpacing: '-0.01125rem' }}
          >
            Advanced settings
          </Text>
        </Group>
      </Group>
      <Collapse in={opened}>
        <Box px="md">
          <FeeInputs />
        </Box>
      </Collapse>
    </>
  );
};

const FeeInputs = () => {
  return (
    <Stack>
      <FeeInputContainer
        title="Recognize on"
        info="Choose the date on which to apply the fee. By default, the system will use the cancellation date on cancelled reservations."
      >
        <FeeTriggerEditor />
      </FeeInputContainer>
      <FeeInputContainer title="Tax rate">
        <FeeTaxRateEditor />
      </FeeInputContainer>

      <FeeInputContainer title="Revenue Account">
        <FeeAccountEditor type="credit" />
      </FeeInputContainer>
      <FeeInputContainer title="Revenue Party">
        <FeeAccountPartyEditor type="credit" />
      </FeeInputContainer>

      <FeeInputContainer title="Expense Account">
        <FeeAccountEditor type="debit" />
      </FeeInputContainer>
      <FeeInputContainer title="Expense Party">
        <FeeAccountPartyEditor type="debit" />
      </FeeInputContainer>
    </Stack>
  );
};

const FeeInputContainer = ({
  children,
  title,
  info,
}: {
  title: string;
  children?: ReactNode;
  info?: string;
}) => {
  return (
    <Group align="flex-start" w="100%" wrap="nowrap">
      <Group gap={rem(8)} miw={150} align="center">
        <Text fw={500} ta="left" mt={8}>
          {title}
        </Text>
        {info && (
          <Tooltip label={info} multiline maw={300}>
            <Icon icon="InfoIcon" size={14} mt={6} display="inline" />
          </Tooltip>
        )}
      </Group>
      <Box flex={1}>{children}</Box>
    </Group>
  );
};
